import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Pagination from 'react-js-pagination';

import Api from '../../Api';
import { withAuth } from '../../hoc/Auth';
import { Column, Container, Wrapper } from '../../hoc/Layout';
import ResponsiveSidebarFilters from '../../Components/Utils/ResponsiveSidebarFilters';
import AdminFiltersToggle from '../../Components/ComponentsOfAdministrator/AdminFiltersToggle';
import Icon from '../../Components/Utils/Icon';
import DateRange from '../../Components/Utils/DateRange';
import Modal from '../../Components/Utils/Modal';
import EmptyCard from '../../hoc/EmptyCard';
import Search from '../../Components/Utils/SearchTags';
import AntiFraudList from '../../Components/ComponentsOfAdministrator/AntiFraudList';
import AntiFraudFilters from '../../Components/ComponentsOfAdministrator/AntiFraudFilters';
import AntiFraudForm from '../../Components/ComponentsOfAdministrator/AntiFraudForm';
import AntiFraudDetails from '../../Components/ComponentsOfAdministrator/AntiFraudDetails';

import classes from './styles.module.css';
import filtersClasses from '../../Components/ComponentsOfAdministrator/AdminFiltersToggle/styles.module.css';

const MODE_RECORDS = 'records';
const MODE_LOGS = 'logs';

const AntiFraudPage = withAuth(function ({ token }) {
    const [mode, setMode] = useState(MODE_RECORDS);
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [list, setList] = useState([]);
    const [logs, setLogs] = useState([]);
    const [selected, setSelected] = useState(null);
    const [stats, setStats] = useState(null);
    const [reference, setReference] = useState({ sources: [], kinds: [] });
    const [sort] = useState('DESC');
    const [filterDate, setFilterDate] = useState({});
    const [filters, setFilters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);

    useEffect(() => {
        new Api({ token }).getAntiFraudReference().then((r) => {
            setReference(r || { sources: [], kinds: [] });
        });
    }, [token]);

    useEffect(() => {
        new Api({ token }).getAntiFraudStats().then((r) => {
            setStats(r.data || null);
        });
    }, [token]);

    useEffect(() => {
        const payload = {
            page: currentPage,
            itemsPerPage: perPage,
            order: sort,
            dateRange: filterDate,
        };

        if (mode === MODE_RECORDS) {
            const sources = [];
            const kinds = [];

            filters.forEach((f) => {
                if (f.key === 'source') sources.push(f.value);
                else if (f.key === 'kind') kinds.push(f.value);
                else if (f.key === 'search') payload.search = f.value;
                else if (f.key === 'isBlocked') payload.isBlocked = f.value === 'true';
            });

            if (sources.length > 0) payload.source = sources;
            if (kinds.length > 0) payload.kind = kinds;

            setLoading(true);
            new Api({ token }).getAntiFraudRecords(payload).then((r) => {
                setList(Array.isArray(r.data) ? r.data : []);
                setTotalCount(typeof r.count === 'number' ? r.count : 0);
                setLoading(false);
            });
        } else {
            filters.forEach((f) => {
                if (f.key === 'result') payload.result = f.value;
            });

            setLoading(true);
            new Api({ token }).getAntiFraudLogs(payload).then((r) => {
                setLogs(Array.isArray(r.data) ? r.data : []);
                setTotalCount(typeof r.count === 'number' ? r.count : 0);
                setLoading(false);
            });
        }
    }, [mode, filters, filterDate, currentPage, perPage, sort, token]);

    function setPage(page, itemsPerPage) {
        setCurrentPage(page || 1);
        if (itemsPerPage) setPerPage(itemsPerPage);
    }

    function onChangeSearch(tags) {
        setFilters(tags);
    }

    function onAdded() {
        setCurrentPage(1);
        setIsOpenModal(false);
        setFilters([...filters]);
    }

    function onSelectRecord(row) {
        setSelected(row || null);
    }

    function handleUnblock(id) {
        new Api({ token }).unblockAntiFraudRecord(id).then(() => {
            setFilters([...filters]);
            setSelected(null);
        });
    }

    function handleDelete(id) {
        if (window.confirm('Удалить запись?')) {
            new Api({ token }).deleteAntiFraudRecord(id).then(() => {
                setFilters([...filters]);
                setSelected(null);
            });
        }
    }

    function switchMode(newMode) {
        if (newMode === mode) return;
        setMode(newMode);
        setCurrentPage(1);
        setSelected(null);
        setFilters([]);
    }

    function formatDate(iso) {
        if (!iso) return '—';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleString('ru-RU');
    }

    return (
        <Wrapper>
            <Modal
                title={<FormattedMessage id="AntiFraud.modal.addTitle" defaultMessage="Добавить блокировку" />}
                onToggleModal={() => setIsOpenModal(!isOpenModal)}
                isOpen={isOpenModal}
                onClose={() => setIsOpenModal(false)}
            >
                <AntiFraudForm token={token} reference={reference} onAdded={onAdded} />
            </Modal>

            <Container>
                <Column width="780" className={filtersClasses.MainColumn}>
                    <AdminFiltersToggle onClick={() => setIsFiltersOpen(true)} />
                    <div className={classes.Tabs}>
                        <div
                            className={`${classes.Tab} ${mode === MODE_RECORDS ? classes.TabActive : ''}`}
                            onClick={() => switchMode(MODE_RECORDS)}
                        >
                            Чёрный список
                        </div>
                        <div
                            className={`${classes.Tab} ${mode === MODE_LOGS ? classes.TabActive : ''}`}
                            onClick={() => switchMode(MODE_LOGS)}
                        >
                            Журнал проверок
                        </div>
                    </div>

                    {mode === MODE_RECORDS && (
                        <Search
                            onChangeSearch={onChangeSearch}
                            onSearch={() => {
                                setFilters((prev) => [...prev]);
                                setCurrentPage(1);
                            }}
                            isDisabled={false}
                            defaultSearch={{
                                key: 'search',
                                locale: 'AntiFraud.search.mask',
                                placeholder: 'AntiFraud.search.maskPlaceholder',
                            }}
                            searchBy={[
                                {
                                    key: 'search',
                                    locale: 'AntiFraud.search.mask',
                                    placeholder: 'AntiFraud.search.maskPlaceholder',
                                },
                            ]}
                        />
                    )}

                    <div className={classes.Card}>
                        <div className={classes.Header}>
                            <h5 className={classes.Title}>
                                {mode === MODE_RECORDS ? (
                                    <React.Fragment>
                                        <FormattedMessage id="AntiFraud.title" defaultMessage="Антифрод записи" />
                                        {stats && (
                                            <span className={classes.StatsInfo}>
                                                ({stats.totalBlocked || 0} заблокировано)
                                            </span>
                                        )}
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        Журнал проверок
                                        {stats && stats.checksLast24h && (
                                            <span className={classes.StatsInfo}>
                                                (за 24ч: {stats.checksLast24h.total || 0} проверок,{' '}
                                                {stats.checksLast24h.blocked || 0} заблокировано)
                                            </span>
                                        )}
                                    </React.Fragment>
                                )}
                            </h5>

                            {mode === MODE_RECORDS && (
                                <div className={classes.Btn} onClick={() => setIsOpenModal(!isOpenModal)}>
                                    <Icon className={classes.IconLeft} icon="add-outline" size={'16px'} />
                                    <FormattedMessage id="AntiFraud.add" defaultMessage="Добавить" />
                                </div>
                            )}
                        </div>

                        {mode === MODE_RECORDS ? (
                            <AntiFraudList
                                isLoading={loading}
                                data={list}
                                currentId={selected ? selected.id : null}
                                onSelect={onSelectRecord}
                                reference={reference}
                            />
                        ) : (
                            <LogsTable loading={loading} logs={logs} formatDate={formatDate} />
                        )}

                        {perPage < totalCount && (
                            <div className={classes.PaginationWrap}>
                                <Pagination
                                    firstPageText="⇤"
                                    prevPageText="⇠"
                                    lastPageText="⇥"
                                    nextPageText="⇢"
                                    activeLinkClass={classes.ActiveLink}
                                    activePage={currentPage}
                                    itemsCountPerPage={perPage}
                                    totalItemsCount={totalCount}
                                    pageRangeDisplayed={5}
                                    onChange={(page) => setPage(page, perPage)}
                                />
                            </div>
                        )}
                    </div>

                    {mode === MODE_RECORDS && selected && (
                        <AntiFraudDetails
                            record={selected}
                            reference={reference}
                            onUnblock={handleUnblock}
                            onDelete={handleDelete}
                        />
                    )}
                </Column>

                <Column width="350" className={filtersClasses.SidebarColumn}>
                    <ResponsiveSidebarFilters
                        title="Фильтры"
                        isOpen={isFiltersOpen}
                        onClose={() => setIsFiltersOpen(false)}
                    >
                        <EmptyCard>
                            <DateRange
                                onSelectDate={(range) => {
                                    setFilterDate(range || {});
                                    setCurrentPage(1);
                                }}
                                defaultPeriod={'zero'}
                            />
                        </EmptyCard>

                        {mode === MODE_RECORDS && (
                            <AntiFraudFilters
                                isLoading={loading}
                                reference={reference}
                                onApplyFilters={(newFilters) => {
                                    setFilters(newFilters || []);
                                    setCurrentPage(1);
                                    setIsFiltersOpen(false);
                                }}
                            />
                        )}

                        {mode === MODE_LOGS && (
                            <div className={classes.StatsCard}>
                                <h6 className={classes.StatsTitle}>Фильтр по результату</h6>
                                <div className={classes.LogsFilterButtons}>
                                    <button
                                        className={`${classes.LogsFilterBtn} ${filters.find((f) => f.key === 'result') ? '' : classes.LogsFilterBtnActive}`}
                                        onClick={() => {
                                            setFilters([]);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        Все
                                    </button>
                                    <button
                                        className={`${classes.LogsFilterBtn} ${filters.some((f) => f.key === 'result' && f.value === 'ok') ? classes.LogsFilterBtnActive : ''}`}
                                        onClick={() => {
                                            setFilters([{ key: 'result', value: 'ok' }]);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        Только OK
                                    </button>
                                    <button
                                        className={`${classes.LogsFilterBtn} ${filters.some((f) => f.key === 'result' && f.value === 'blocked') ? classes.LogsFilterBtnActive : ''}`}
                                        onClick={() => {
                                            setFilters([{ key: 'result', value: 'blocked' }]);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        Только заблокированные
                                    </button>
                                </div>
                            </div>
                        )}

                        {stats && (
                            <div className={classes.StatsCard}>
                                <h6 className={classes.StatsTitle}>
                                    <FormattedMessage id="AntiFraud.stats.title" defaultMessage="Статистика" />
                                </h6>
                                <div className={classes.StatsList}>
                                    <div className={classes.StatsItem}>
                                        <span>Всего записей:</span>
                                        <strong>{stats.total || 0}</strong>
                                    </div>
                                    <div className={classes.StatsItem}>
                                        <span>Заблокировано:</span>
                                        <strong>{stats.totalBlocked || 0}</strong>
                                    </div>
                                    <div className={classes.StatsItem}>
                                        <span>Проверок за 24ч:</span>
                                        <strong>{(stats.checksLast24h && stats.checksLast24h.total) || 0}</strong>
                                    </div>
                                    <div className={classes.StatsItem}>
                                        <span>Блокировок за 24ч:</span>
                                        <strong>{(stats.checksLast24h && stats.checksLast24h.blocked) || 0}</strong>
                                    </div>
                                </div>
                            </div>
                        )}
                    </ResponsiveSidebarFilters>
                </Column>
            </Container>
        </Wrapper>
    );
});

function LogsTable({ loading, logs, formatDate }) {
    if (loading) {
        return <div className={classes.LogsEmpty}>Загрузка...</div>;
    }
    if (!logs || logs.length === 0) {
        return <div className={classes.LogsEmpty}>Нет проверок за выбранный период</div>;
    }
    return (
        <React.Fragment>
            <div className={classes.TableScroll}>
                <table className={classes.LogsTable}>
                    <thead>
                        <tr>
                            <th>Дата</th>
                            <th>Тип</th>
                            <th>Результат</th>
                            <th>Tx</th>
                            <th>Причина</th>
                            <th>Время (мс)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map((log) => (
                            <tr key={log.id}>
                                <td>{formatDate(log.createdAt)}</td>
                                <td>{log.checkType || '—'}</td>
                                <td>
                                    <span
                                        className={
                                            log.result === 'blocked'
                                                ? classes.ResultBlocked
                                                : log.result === 'ok'
                                                  ? classes.ResultOk
                                                  : classes.ResultOther
                                        }
                                    >
                                        {log.result || '—'}
                                    </span>
                                </td>
                                <td>{log.transactionId || '—'}</td>
                                <td title={log.blockedReason || ''}>
                                    {log.blockedReason ? `${log.blockedBy || ''}: ${log.blockedReason}`.slice(0, 80) : '—'}
                                </td>
                                <td>{log.totalTimeMs != null ? log.totalTimeMs : '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className={classes.LogsMobileList}>
                {logs.map((log) => (
                    <div key={log.id} className={classes.LogsMobileCard}>
                        <div className={classes.LogsMobileRow}>
                            <div className={classes.LogsMobilePrimary}>{formatDate(log.createdAt)}</div>
                            <span
                                className={
                                    log.result === 'blocked'
                                        ? classes.ResultBlocked
                                        : log.result === 'ok'
                                          ? classes.ResultOk
                                          : classes.ResultOther
                                }
                            >
                                {log.result || '—'}
                            </span>
                        </div>
                        <div className={classes.LogsMobileRow}>
                            <div className={classes.LogsMobileSecondary}>{log.checkType || '—'}</div>
                            <div className={classes.LogsMobileMuted}>
                                {log.totalTimeMs != null ? `${log.totalTimeMs} мс` : '—'}
                            </div>
                        </div>
                        <div className={classes.LogsMobileValue}>{log.transactionId || '—'}</div>
                        <div className={classes.LogsMobileReason}>
                            {log.blockedReason ? `${log.blockedBy || ''}: ${log.blockedReason}` : '—'}
                        </div>
                    </div>
                ))}
            </div>
        </React.Fragment>
    );
}

export default AntiFraudPage;



// WEBPACK FOOTER //
// ./src/PagesOfAdministrator/AntiFraud/index.js